/**
 * mobile.js — 行動版路由設定（iPad 平板）
 *
 * 功能範圍（依需求規格）：
 *   1. 客戶智能搜尋
 *      (1) 企業標籤搜尋   → /m/ctm
 *      (2) 地理位置搜尋   → /m/map
 *   2. 客戶名單管理
 *      (1) 潛在客戶名單管理 → /m/cust
 *   3. 分行及標籤管理
 *      (1) 拜訪行事曆     → /m/cal
 *      (2) 公司概況表     → /m/combrief
 *
 * 架構說明：
 *   - 登入 / 登出頁為頂層路由（不套用 MobileHome layout）
 *   - /m 為巢狀路由根（使用 MobileHome.vue 作為 layout shell）
 *     子路由預設重新導向至 /m/ctm（企業標籤搜尋）
 *   - 主功能頁與子頁皆顯示底部 TabBar
 *   - 子頁（hideNav: true）僅控制顯示返回按鈕
 *
 * 目前暫以現有桌面版 view 作為佔位元件（標註 TODO），
 * 待各行動版專屬 view（src/views/mobile/ctm/）建立後，逐一替換 import 路徑。
 */

const mobileRoutes = [

  // ── 登出（不套用 MobileHome layout）──────────────────────────
  {
    path: '/m/logout',
    name: 'm-logout',
    component: () => import('../views/Logout.vue'),
    meta: {
      mobile: true,
      hideMenu: true,
      hideBreadcrumb: true,
    },
  },
  {
    path: '/m/logoutauto',
    name: 'm-logoutauto',
    component: () => import('../views/Logoutauto.vue'),
    meta: {
      mobile: true,
      hideMenu: true,
      hideBreadcrumb: true,
    },
  },

  // ══════════════════════════════════════════════════════════════
  //  /m  — MobileHome layout shell（巢狀路由）
  //  App.vue 的 router-view 渲染 MobileHome，
  //  MobileHome 內部的 router-view 渲染各子功能頁
  // ══════════════════════════════════════════════════════════════
  {
    path: '/m',
    component: () => import('../views/mobile/MobileHome.vue'),
    // 父路由 meta 設定 hideMenu/hideBreadcrumb，
    // App.vue 使用 $route.matched.some() 讀取，所以子路由也會繼承效果
    meta: {
      mobile: true,
      hideMenu: true,
      hideBreadcrumb: true,
    },
    // /m 直接訪問時，預設進入企業標籤搜尋
    redirect: '/m/ctm',
    children: [

      // ══════════════════════════════════════════════════════════
      //  1. 客戶智能搜尋
      // ══════════════════════════════════════════════════════════

      // ── (1) 企業標籤搜尋 ────────────────────────────────────────
      {
        path: 'ctm',
        name: 'm-ctm',
        component: () => import('../views/mobile/ctm/MobileCtm.vue'),
        meta: {
          mobile: true,
          title: '企業標籤搜尋',
          group: '客戶智能搜尋',
          tabIndex: 0,
        },
      },

      // 企業標籤搜尋 → 相似企業明細（子頁）
      // {
      //   path: 'ctm-detail',
      //   name: 'm-ctm-detail',
      //   component: () => import('../views/ctm/ctmDetail.vue'),
      //   meta: {
      //     mobile: true,
      //     title: '相似企業',
      //     group: '客戶智能搜尋',
      //     hideNav: true,
      //   },
      // },

      // 企業標籤搜尋 → 公司詳細資訊（子頁）
      // {
      //   path: 'ctm-map-com',
      //   name: 'm-ctm-map-com',
      //   component: () => import('../views/ctm/ctmMapCom.vue'),
      //   meta: {
      //     mobile: true,
      //     title: '企業標籤資訊',
      //     group: '客戶智能搜尋',
      //     hideNav: true,
      //   },
      // },

      // ── (2) 地理位置搜尋 ────────────────────────────────────────
      {
        path: 'map',
        name: 'm-ctm-map',
        component: () => import('../views/mobile/ctm/MobileCtmMap.vue'),
        meta: {
          mobile: true,
          title: '地理位置搜尋',
          group: '客戶智能搜尋',
          tabIndex: 1,
        },
      },

      // 地理位置搜尋 → 公司詳細資訊（子頁）
      {
        path: 'map-com',
        name: 'm-map-com',
        component: () => import('../views/ctm/ctmMapCom.vue'),
        meta: {
          mobile: true,
          title: '企業標籤資訊',
          group: '客戶智能搜尋',
          hideNav: true,
        },
      },

      // ══════════════════════════════════════════════════════════
      //  2. 客戶名單管理
      // ══════════════════════════════════════════════════════════

      // ── (1) 潛在客戶名單管理 ──────────────────────────────────────
      {
        path: 'cust',
        name: 'm-cust-mgmt',
        component: () => import('../views/mobile/ctm/MobileCxMgmt.vue'),
        meta: {
          mobile: true,
          title: '潛在客戶名單管理',
          group: '客戶名單管理',
          tabIndex: 2,
        },
      },

      // ── (2) 案件管理 ──────────────────────────────────────────────
      {
        path: 'case',
        name: 'm-case-mgmt',
        component: () => import('../views/mobile/ctm/MobileCaseMgmt.vue'),
        meta: {
          mobile: true,
          title: '案件管理',
          group: '客戶名單管理',
          tabIndex: 3,
        },
      },

      // ══════════════════════════════════════════════════════════
      //  3. 分行及標籤管理
      // ══════════════════════════════════════════════════════════

      // ── (1) 拜訪行事曆 ────────────────────────────────────────────
      {
        path: 'cal',
        name: 'm-cal-mgmt',
        component: () => import('../views/mobile/ctm/MobileCalMgmt.vue'),
        meta: {
          mobile: true,
          title: '拜訪行事曆',
          group: '分行及標籤管理',
          tabIndex: 4,
        },
      },

      // ── (2) 公司概況表 ────────────────────────────────────────────
      {
        path: 'combrief',
        name: 'm-com-brief',
        // TODO: 替換為 () => import('../views/mobile/ctm/MobileComDetail.vue')
        component: () => import('../views/ctm/comDetail.vue'),
        meta: {
          mobile: true,
          title: '公司概況表',
          group: '分行及標籤管理',
          tabIndex: 5,
        },
      },

    ], // end children
  }, // end /m layout route

  // ── 行動版未知路由 ─────────────────────────────────────────
  {
    path: '/m/*',
    redirect: '/404',
  },
];

export default mobileRoutes;
